import { useEffect, useState } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
import { useOutletContext } from 'react-router-dom';
import { MediaViewerOutletContextState } from '@layouts/types';

// Fraction of the server's grant TTL after which we proactively
// re-mint, so a user who keeps the review tab open for hours doesn't
// hit a sudden 403 mid-browsing. The server config is the source of
// truth for absolute expiry; this just controls the renewal cadence.
const RENEW_AT_FRACTION = 0.5;

// Default TTL we assume before the first successful response. The
// backend currently grants 600s; if it changes we'll learn the new
// value from `Max-Age` on the next renewal cycle (the cookie is
// HttpOnly so we read it from `Max-Age` parsed off the headers).
const DEFAULT_TTL_SECONDS = 600;

interface GrantState {
  /** True once at least one grant has been minted this session. */
  ready: boolean;
  /** Last error reCAPTCHA / the grant call surfaced, if any. */
  error: string | null;
}

/**
 * Mints a server-side grant cookie that authorises the proxy iframe
 * to render this user's review session, and re-mints it before
 * expiry. The hook returns `ready=false` until the first mint
 * succeeds; render the iframe behind a `ready` gate so its first
 * request already carries the cookie.
 *
 * Failures bubble up via `error`. The caller decides UX (retry
 * button, error banner, etc.).
 */
export function useProxyGrant(): GrantState {
  const { mintWebsiteProxyGrant } =
    useOutletContext<MediaViewerOutletContextState>();
  const [cookieConsent, setCookieConsent] = useState<boolean>(
    CookieConsent.acceptedCategory('necessary')
  );
  useEffect(() => {
    if (cookieConsent) return;
    const interval = setInterval(() => {
      if (CookieConsent.acceptedCategory('necessary')) {
        setCookieConsent(true);
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [cookieConsent]);

  const [state, setState] = useState<GrantState>({
    ready: false,
    error: null
  });

  useEffect(() => {
    let cancelled = false;
    let renewalTimer: ReturnType<typeof setTimeout> | undefined;

    const cycle = async () => {
      try {
        const result = await mintWebsiteProxyGrant();
        if (cancelled) return;
        setState({ ready: true, error: result === true ? null : result.error });
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({
          ready: s.ready,
          error: (err as Error).message
        }));
      }
      if (cancelled) return;
      // Re-mint at half the TTL so the cookie is always fresh well
      // before the server stops accepting it. Using a timeout
      // (rather than `setInterval`) guarantees we never overlap
      // requests if a previous mint stalled past its half-TTL.
      const renewIn = DEFAULT_TTL_SECONDS * 1000 * RENEW_AT_FRACTION;
      renewalTimer = setTimeout(cycle, renewIn);
    };

    if (cookieConsent) cycle();
    return () => {
      cancelled = true;
      if (renewalTimer !== undefined) clearTimeout(renewalTimer);
    };
  }, [cookieConsent, mintWebsiteProxyGrant]);

  return state;
}
